<template>
    <Card class="snippets" :role="ariaRole" :aria-labelledby="ariaLabelledBy" header="Snippets">
        <template slot="header">
            <description-popover
                ariaLabel="Help on how to use snippets"
                :asSuperscript="false"
                placement="left"
            >
                <p>
                    Edit your snippets here. When you type the name in a field that supports
                    snippets and then press <kbd>Tab</kbd>, the typed name is replaced with the
                    snippet's replacement text.
                </p>

                <p>
                    To create a new snippet, click the
                    <Icon :icon="icons.Plus" />. In the popup you can set the name and replacement
                    text of the snippet. Snippet names may not contain spaces and must be unique.
                </p>

                <p>Click the <Icon :icon="icons.Edit" /> button to edit an existing snippet.</p>

                <p>Finally, <Icon :icon="icons.Delete" /> deletes a snippet.</p>
            </description-popover>
        </template>
        <loader :scale="1" class="m-3" v-if="loading" />
        <cg-error
            class="m-0 rounded-t-none"
            v-else-if="error != null"
            :error="error.error"
            :doRetry="error.retry"
        />
        <div class="snippet-manager @container" v-else>
            <div class="form-layout-row mb-3">
                <FormField id="search-snippets" class="filter-group">
                    <FormLabel>Search Snippets</FormLabel>
                    <search-input
                        :query="filter"
                        resource="snippet"
                        @input="
                            newFilter => {
                                filter = newFilter.query;
                            }
                        "
                    />
                </FormField>
                <accessible-logs
                    :for="tableId"
                    title="Update history of snippets"
                    :logs="this.statusMessages"
                />
                <accessible-status
                    :for="tableId"
                    :content="`${this.filteredSnippets.length} ${
                        this.filteredSnippets.length === 1 ? 'snippet' : 'snippets'
                    } currently showing. `"
                >
                </accessible-status>
            </div>
            <div class="max-w-full overflow-auto">
                <table :id="tableId" class="table table-striped snippets-table">
                    <thead>
                        <tr>
                            <th scope="col">Name</th>
                            <th scope="col">Replacement text</th>
                            <td class="border-b">
                                <div class="flex items-stretch justify-end gap-1">
                                    <promise-button
                                        v-if="editable"
                                        loadingAlertMessage="Adding snippet"
                                        successAlertMessage="Snippet added"
                                        class="add-button"
                                        :prominence="5"
                                        :action="addNewSnippet"
                                    >
                                        <Icon :icon="icons.Plus" />
                                        <span class="whitespace-nowrap">New Snippet</span>
                                    </promise-button>
                                </div>
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="snippet in filteredSnippets" :key="snippet.id">
                            <td class="snippet-key">{{ snippet.key }}</td>
                            <td class="snippet-value">{{ snippet.value }}</td>
                            <td class="snippet-actions">
                                <div
                                    v-b-popover.top.hover="
                                        editable ? '' : 'You are not allowed to edit snippets.'
                                    "
                                    class="flex items-stretch justify-end gap-1"
                                >
                                    <promise-button
                                        variant="danger"
                                        data-cy="delete-snippet-button"
                                        class="size-9"
                                        semantic="critical"
                                        :prominence="4"
                                        :confirm="{
                                            type: 'dialog',
                                            message: `Are you sure you want to delete this snippet?`,
                                        }"
                                        :disabled="!editable"
                                        :action="
                                            () =>
                                                deleteSnippet(snippet).then(() =>
                                                    afterDeleteSnippet(snippet),
                                                )
                                        "
                                        loadingAlertMessage="Deleting snippet"
                                        successAlertMessage="Snippet deleted"
                                    >
                                        <Icon :icon="icons.Delete" />
                                        <span class="sr-only"
                                            >Delete snippet "{{ snippet.key }}"</span
                                        >
                                    </promise-button>
                                    <button
                                        class="edit-snippet-button cg-btn btn-actionable-4 size-9"
                                        :disabled="!editable"
                                        @click="editSnippet(snippet)"
                                    >
                                        <Icon :icon="icons.Edit" />
                                        <span class="sr-only"
                                            >Edit snippet "{{ snippet.key }}"</span
                                        >
                                    </button>
                                </div>
                            </td>
                        </tr>

                        <tr v-if="filteredSnippets.length === 0">
                            <td class="p-3 text-center font-italic text-muted" colspan="3">
                                <template v-if="snippets.length === 0">
                                    You have not created any snippets yet!
                                </template>
                                <template v-else> No snippets found! </template>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <b-modal
                title-tag="h2"
                title-class="title-medium"
                :title="modalTitle"
                ref="modal"
                class="edit-snippet-modal"
                dialog-class="push-down"
                static
                @shown="focusInput"
            >
                <div class="form-layout-stacked">
                    <FormField v-if="editingSnippet != null">
                        <FormLabel>Snippet Name</FormLabel>
                        <FormControl
                            type="text"
                            placeholder="Snippet Name"
                            name="snippet-key"
                            @keydown.native="clickSaveOnCtrlEnter"
                            v-model="editingSnippet.key"
                            ref="keyInput"
                        />
                    </FormField>

                    <FormField v-if="editingSnippet != null">
                        <FormLabel>Snippet Value</FormLabel>
                        <FormTextarea
                            rows="10"
                            name="snippet-value"
                            placeholder="Replacement text"
                            @keydown.native="clickSaveOnCtrlEnter"
                            v-model="editingSnippet.value"
                            ref="valueInput"
                        />
                    </FormField>
                </div>

                <template slot="modal-footer">
                    <b-button-toolbar justify class="flex-grow-1">
                        <b-button variant="danger" @click="cancelEditSnippet"> Cancel </b-button>

                        <promise-button
                            ref="saveButton"
                            :confirm="saveConfirmMessage"
                            :action="saveSnippet"
                            loadingAlertMessage="Saving snippet"
                            successAlertMessage="Snippet saved"
                        >
                            Save
                        </promise-button>
                    </b-button-toolbar>
                </template>
            </b-modal>
        </div>
    </Card>
</template>

<script>
import { Delete, Edit, Plus } from '@codegrade/icons';
import { Maybe } from '@codegrade/maybe';

import { PromiseButton } from '@/components/Button';
import { Card } from '@/components/Card';
import CgError from '@/components/Error';
import * as Form from '@/components/Form';
import { Icon, mapIcons } from '@/components/Icon';
import Loader from '@/components/Loader';
import { DescriptionPopover } from '@/components/Popover';
import SearchInput from '@/components/SearchInput';
import { AccessibleLogs, AccessibleStatus } from '@/components/VisuallyHiddenLiveRegions';
import * as models from '@/models';
import { CourseSnippetsStore, UserStore } from '@/store';
import * as utils from '@/utils';

export default {
    name: 'snippet-manager',

    props: {
        course: {
            type: models.Course,
            default: null,
        },

        editable: {
            type: Boolean,
            default: true,
        },

        /** The ARIA role the component should have. */
        ariaRole: {
            type: String,
            required: false,
        },

        /** The ARIA labelled-by property for this component. */
        ariaLabelledBy: {
            type: String,
            required: false,
        },
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        return {
            loading: true,
            filter: this.$route.query.filterSnippets || '',
            editingSnippet: null,
            error: null,
            statusMessages: [],
            tableId: `snippets-manager-${utils.getUniqueId()}`,
        };
    },

    computed: {
        ...mapIcons({ Delete, Edit, Plus }),

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        storeSnippets() {
            if (this.course) {
                return CourseSnippetsStore.get()(this.course.id).orDefault([]);
            } else {
                return UserStore.snippets();
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        snippets() {
            return Object.values(this.storeSnippets).sort((a, b) => utils.cmpNoCase(a.key, b.key));
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        filteredSnippets() {
            if (!this.filter) {
                return this.snippets;
            } else {
                const filter = this.filter.toLowerCase();
                return this.snippets.filter(
                    s => s.key.toLowerCase().match(filter) || s.value.toLowerCase().match(filter),
                );
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        modalTitle() {
            if (!this.editingSnippet) {
                return '';
            } else if (this.editingSnippet.id == null) {
                return 'Add snippet';
            } else {
                return 'Edit snippet';
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        baseUrl() {
            if (this.course == null) {
                return '/api/v1/';
            } else {
                return `/api/v1/courses/${this.course.id}/`;
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        courseId() {
            return utils.getProps(this.course, null, 'id');
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        clickSaveOnCtrlEnter() {
            return utils.ctrlOrCmd('Enter', () => {
                this.$withRef('saveButton', el => {
                    el.$el.click();
                });
            });
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        saveConfirmMessage() {
            const snippet = this.editingSnippet;

            if (snippet == null) {
                return false;
            }

            const dup = this.getSnippetWithSameKey(snippet);

            if (dup) {
                let replacement = dup.value;
                if (replacement.length > 100) {
                    replacement = `${replacement.slice(0, 100)}${utils.ELLIPSIS}`;
                }

                return {
                    type: 'dialog',
                    message: `There already exists a snippet with the same name and replacement text "${replacement}". Do you want to overwrite that snippet?`,
                };
            } else {
                return false;
            }
        },
    },

    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        editSnippet(snippet) {
            if (snippet == null) {
                this.editingSnippet = {
                    key: '',
                    value: '',
                };
            } else {
                this.editingSnippet = { ...snippet };
            }

            this.$refs.modal.show();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        cancelEditSnippet() {
            this.$refs.modal.hide();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        ensureValidSnippet(snippet) {
            const { key, value } = snippet;

            if (!key) {
                throw new Error('The snippet name may not be empty.');
            }

            if (key.match(/\s/)) {
                throw new Error(
                    'Snippet names may not contain spaces. If you want to use ' +
                        'a multiple words in a name you can separate them with a ' +
                        'dash or an underscore.',
                );
            }

            if (!value) {
                throw new Error('The snippet replacement text may not be empty.');
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getSnippetWithSameKey(snippet) {
            const { id, key } = snippet;

            return this.snippets.find(s => s.key === key && s.id !== id);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        isSnippetUnchanged(snippet) {
            const { id, key, value } = snippet;

            const oldSnippet = this.snippets.find(s => s.id === id);

            return oldSnippet && key === oldSnippet.key && value === oldSnippet.value;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async saveSnippet() {
            const snippet = this.editingSnippet;

            this.ensureValidSnippet(snippet);

            if (this.isSnippetUnchanged(snippet)) {
                return Promise.resolve(snippet);
            }

            const dup = this.getSnippetWithSameKey(snippet);
            if (dup) {
                const oldSnippet = { ...snippet };
                snippet.id = dup.id;
                let req = this.updateSnippet(snippet);
                if (oldSnippet.id != null) {
                    req = req
                        .then(() => this.deleteSnippet(oldSnippet))
                        .then(this.afterDeleteSnippet);
                }
                return req;
            } else if (snippet.id == null) {
                return this.addSnippet(snippet);
            } else {
                return this.updateSnippet(snippet);
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async addSnippet(snippet) {
            const response = await this.$http.put(this.getSnippetRoute('snippet'), snippet);

            snippet.id = response.data.id;

            if (this.courseId) {
                CourseSnippetsStore.commitAddSnippet({
                    courseId: this.course.id,
                    snippet,
                });
            } else {
                UserStore.addSnippet(snippet);
            }

            this.cancelEditSnippet();
            this.statusMessages.push(`New snippet "${snippet.key}" added. `);

            return response;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async updateSnippet(snippet) {
            const response = await this.$http.patch(
                this.getSnippetRoute(`snippets/${snippet.id}`),
                snippet,
            );

            if (this.courseId) {
                CourseSnippetsStore.commitUpdateSnippet({
                    courseId: this.course.id,
                    snippet,
                });
            } else {
                UserStore.updateSnippet(snippet);
            }

            this.cancelEditSnippet();
            this.statusMessages.push(`Existing snippet "${snippet.key}" updated. `);

            return response;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        deleteSnippet(snippet) {
            return this.$http
                .delete(this.getSnippetRoute(`snippets/${snippet.id}`))
                .then(() => snippet);
        },

        /** @returns A promise after adding a new snippet. */
        addNewSnippet() {
            this.editSnippet(null);
            return Promise.resolve();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        afterDeleteSnippet(snippet) {
            if (this.courseId) {
                CourseSnippetsStore.commitDeleteSnippet({
                    courseId: this.course.id,
                    snippet,
                });
            } else {
                UserStore.deleteSnippet(snippet);
            }
            this.statusMessages.push(`Snippet "${snippet.key}" deleted. `);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getSnippetRoute(routeEnd) {
            return `${this.baseUrl}${routeEnd}`;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async refreshSnippets() {
            this.loading = true;
            this.error = null;
            try {
                if (this.courseId) {
                    await CourseSnippetsStore.load({ courseId: this.courseId });
                } else {
                    await UserStore.refreshSnippets();
                }
            } catch (caught) {
                this.error = { error: caught, retry: this.refreshSnippets };
            } finally {
                this.loading = false;
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async focusInput() {
            Maybe.fromNullable(
                await this.$waitForRef(this.editingSnippet.id == null ? 'keyInput' : 'valueInput'),
            )
                .filter(ref => utils.isVueComponent(ref))
                .chain(component => Maybe.fromNullable(component.$el))
                .ifJust($input => {
                    $input.focus();
                });
        },
    },

    watch: {
        courseId: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            async handler() {
                this.refreshSnippets();
            },
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        filter() {
            const newQuery = { ...this.$route.query };
            newQuery.filterSnippets = this.filter || undefined;

            this.$router.replace({
                query: newQuery,
                hash: this.$route.hash,
            });
        },
    },

    components: {
        Card,
        FormField: Form.Field,
        FormLabel: Form.Label,
        FormControl: Form.Input,
        FormTextarea: Form.Textarea,
        FormDesc: Form.Description,
        Icon,
        Loader,
        PromiseButton,
        CgError,
        DescriptionPopover,
        AccessibleStatus,
        AccessibleLogs,
        SearchInput,
    },
};
</script>

<style lang="less" scoped>
.snippets-table {
    margin-bottom: 0;

    th,
    td {
        vertical-align: middle;
    }

    .snippet-key {
        width: 25%;
    }

    .snippet-value {
        width: 75%;
        max-width: 0px;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }
}
</style>

<style lang="less">
.edit-snippet-modal {
    position: initial !important;
}
</style>
